import { ClientEndpoints, EventTrigger, EventTriggerClientEndpoint, EventTriggerFacility, EventTriggerDevice, EventTriggers, LuxcomClientEndpoint, LuxcomEndpointConfig, LuxcomEndpointConfigDetector, MaxServerIntValue } from "@app/shared";
import { useCallback, useMemo, useState } from "react";
import { useFetch } from "@app/shared/src/hooks";
import { Icons, PageContainer } from "@app/dashboard/src/components/shared";
import { SkeletonGrid } from "@app/dashboard/src/components/shared/loading";
import { BiuTabs } from "./BiuTabs";
import { BiuDetectorTable } from "./BiuDetectorTable";
import { BiuDetectorRowData, BIU_NUMBERS, parseLuxcomEndpointConfig, transformToBiuData, isLuxcomEndpoint, getTriggerTypeConfig } from "./types";

type LuxcomBiuSettingsProps = {
    organizationId: string;
    facilityId?: string;
    refreshKey?: number;
    endpointRefreshKey?: number;
    onDataChange?: () => void;
    offlineMode?: boolean;
};

export const LuxcomBiuSettings: React.FC<LuxcomBiuSettingsProps> = ({ organizationId, facilityId, refreshKey, endpointRefreshKey, onDataChange, offlineMode }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);

    const [endpoints, , endpointsHelpers] = useFetch(() => ClientEndpoints.getAll(organizationId, facilityId), [organizationId, facilityId, refreshKey, endpointRefreshKey]);

    const [eventTriggers, , triggersHelpers] = useFetch(() => EventTriggers.getByFacilityId(organizationId, facilityId), [organizationId, facilityId, refreshKey]);

    const luxcomEndpoints = useMemo(() => {
        if (!endpoints) return [];
        const found = endpoints.filter((e) => isLuxcomEndpoint(e) && (!facilityId || e.facilityId === facilityId)) as LuxcomClientEndpoint[];
        return found;
    }, [endpoints, facilityId]);

    const luxcomEndpoint = luxcomEndpoints.length > 0 ? luxcomEndpoints[0] : undefined;

    const biuData = useMemo(() => {
        const result = transformToBiuData(eventTriggers, luxcomEndpoints);
        return result;
    }, [eventTriggers, luxcomEndpoints]);

    const isLoading = endpointsHelpers.isLoading || triggersHelpers.isLoading;

    const handleSave = useCallback(
        async (data: BiuDetectorRowData, isNew: boolean) => {
            if (!luxcomEndpoint?.id) return;
            setIsSaving(true);
            setSaveError(null);

            try {
                const facilityIdsToSave = data.facilityIds ?? [];
                if (facilityId && !facilityIdsToSave.includes(facilityId)) {
                    facilityIdsToSave.push(facilityId);
                }

                let deviceIdsToSave = data.deviceIds ?? [];
                if (offlineMode && deviceIdsToSave.length === 0) {
                    deviceIdsToSave = [];
                }

                if (isNew) {
                    const triggerConfig = getTriggerTypeConfig(data.triggerType);
                    const newTrigger: Partial<EventTrigger> = {
                        $type: data.triggerType,
                        triggerType: triggerConfig?.triggerType,
                        organizationId,
                        detectionZoneId: data.detectionZoneId,
                        screenLineId: data.screenLineId,
                        targetFilter: data.targetFilter ?? MaxServerIntValue,
                        processOnDevice: true, // always true for BIU/Luxcom triggers
                        minValue: data.minValue,
                        maxValue: data.maxValue,
                        additionalValue: data.additionalValue,
                        eventTriggerFacilities: facilityIdsToSave.map((fid) => ({ facilityId: fid }) as EventTriggerFacility),
                        eventTriggerDevices: deviceIdsToSave.map((did) => ({ deviceId: did }) as EventTriggerDevice),
                        eventTriggerClientEndpoints: [
                            {
                                clientEndpointId: luxcomEndpoint.id,
                                endpointConfig: JSON.stringify(
                                    new LuxcomEndpointConfig({
                                        biuNum: data.biuNum,
                                        detectors: [
                                            new LuxcomEndpointConfigDetector({
                                                detector: data.detectorNum,
                                                expression: data.expression ?? "",
                                            }),
                                        ],
                                    }),
                                ),
                            } as EventTriggerClientEndpoint,
                        ],
                    };

                    await EventTriggers.update(newTrigger as EventTrigger);
                } else {
                    const existingTrigger = data.sourceEventTrigger;
                    if (!existingTrigger) {
                        console.error("Cannot update: missing source trigger");
                        return;
                    }

                    existingTrigger.$type = data.triggerType;
                    existingTrigger.detectionZoneId = data.detectionZoneId;
                    existingTrigger.screenLineId = data.screenLineId;
                    existingTrigger.targetFilter = data.targetFilter ?? MaxServerIntValue;
                    existingTrigger.processOnDevice = true;
                    existingTrigger.minValue = data.minValue;
                    existingTrigger.maxValue = data.maxValue;
                    existingTrigger.additionalValue = data.additionalValue;
                    existingTrigger.eventTriggerFacilities = facilityIdsToSave.map((fid) => ({ facilityId: fid, eventTriggerId: existingTrigger.id }) as EventTriggerFacility);
                    existingTrigger.eventTriggerDevices = deviceIdsToSave.map((did) => ({ deviceId: did, eventTriggerId: existingTrigger.id }) as EventTriggerDevice);

                    const etc = existingTrigger.eventTriggerClientEndpoints?.find((e) => e.clientEndpointId === luxcomEndpoint.id);

                    if (etc) {
                        const existingConfig = parseLuxcomEndpointConfig(etc.endpointConfig);
                        const detectors = existingConfig?.detectors ?? [];

                        const detectorIndex = detectors.findIndex((d) => d.detector === data.detectorNum);
                        const updatedDetector = new LuxcomEndpointConfigDetector({
                            detector: data.detectorNum,
                            expression: data.expression ?? "",
                        });

                        if (detectorIndex >= 0) {
                            detectors[detectorIndex] = updatedDetector;
                        } else {
                            detectors.push(updatedDetector);
                        }

                        etc.endpointConfig = JSON.stringify(
                            new LuxcomEndpointConfig({
                                biuNum: data.biuNum,
                                detectors,
                            }),
                        );
                    }

                    await EventTriggers.update(existingTrigger);
                }

                await triggersHelpers.refreshData();
                onDataChange?.();
            } catch (error: any) {
                console.error("Failed to save detector configuration:", error);
                const apiError = error as { error?: string; message?: string };
                const errorMessage = apiError?.error || apiError?.message || "Failed to save. Please check your input.";
                setSaveError(errorMessage);
            } finally {
                setIsSaving(false);
            }
        },
        [luxcomEndpoint, organizationId, facilityId, triggersHelpers, onDataChange, offlineMode],
    );

    const handleDelete = useCallback(
        async (data: BiuDetectorRowData) => {
            if (!luxcomEndpoint?.id || !data.sourceEventTrigger) return;
            setIsSaving(true);

            try {
                const existingTrigger = data.sourceEventTrigger;
                const etc = existingTrigger.eventTriggerClientEndpoints?.find((e) => e.clientEndpointId === luxcomEndpoint.id);

                if (!etc) {
                    console.error("Cannot delete: missing endpoint config");
                    return;
                }

                const existingConfig = parseLuxcomEndpointConfig(etc.endpointConfig);
                const detectors = existingConfig?.detectors ?? [];

                const updatedDetectors = detectors.filter((d) => d.detector !== data.detectorNum);

                if (updatedDetectors.length === 0) {
                    const otherEndpoints = existingTrigger.eventTriggerClientEndpoints?.filter((e) => e.clientEndpointId !== luxcomEndpoint.id);

                    if (!otherEndpoints || otherEndpoints.length === 0) {
                        await EventTriggers.deleteTrigger(existingTrigger.id);
                    } else {
                        existingTrigger.eventTriggerClientEndpoints = otherEndpoints;
                        await EventTriggers.update(existingTrigger);
                    }
                } else {
                    etc.endpointConfig = JSON.stringify(
                        new LuxcomEndpointConfig({
                            biuNum: data.biuNum,
                            detectors: updatedDetectors,
                        }),
                    );
                    await EventTriggers.update(existingTrigger);
                }

                await triggersHelpers.refreshData();
                onDataChange?.();
            } catch (error) {
                console.error("Failed to delete detector configuration:", error);
            } finally {
                setIsSaving(false);
            }
        },
        [luxcomEndpoint, triggersHelpers, onDataChange],
    );

    if (!isLoading && luxcomEndpoints.length === 0) {
        return null;
    }

    const tabs = BIU_NUMBERS.map((biuNum) => ({
        title: `BIU ${biuNum}`,
        content: <BiuDetectorTable biuNum={biuNum} data={biuData} organizationId={organizationId} facilityId={facilityId} luxcomEndpointId={luxcomEndpoint?.id} onSave={handleSave} onDelete={handleDelete} offlineMode={offlineMode} />,
    }));

    return (
        <PageContainer
            Icon={Icons.NetworkWired}
            title="SDLC (Luxcom) Settings"
            titleRight={
                isSaving && (
                    <div className="flex items-center text-sm text-gray-500">
                        <Icons.Spinner className="animate-spin w-4 h-4 mr-2" />
                        Saving...
                    </div>
                )
            }
        >
            {saveError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start justify-between">
                    <div className="flex items-start">
                        <Icons.Warning className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-red-700 text-sm">{saveError}</span>
                    </div>
                    <button onClick={() => setSaveError(null)} className="text-red-500 hover:text-red-700 ml-4">
                        <Icons.Close className="w-4 h-4" />
                    </button>
                </div>
            )}
            {isLoading ? <SkeletonGrid cols={6} rows={5} /> : <BiuTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />}
        </PageContainer>
    );
};
